import { useEffect, useId, useMemo, useState } from "react";
import { useMatch, useNavigate, useSearchParams } from "react-router-dom";

import { useVariants } from "../lib/api";
import type { Variant } from "../lib/api";
import { useAuthConfig, useCurrentUser, useLogout } from "../lib/auth";


export default function TopBar() {
  const navigate = useNavigate();
  const authCfg = useAuthConfig();
  const me = useCurrentUser();
  const logout = useLogout();
  const authOn = authCfg.data?.enabled !== false;
  // useParams() returns {} outside the matched Route; useMatch reads the URL directly.
  const match = useMatch("/fleet/:variantId");
  const variantId = match?.params.variantId;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCode = searchParams.get("code") ?? "";
  const { data, isLoading, isError } = useVariants();
  const variants: Variant[] = data?.variants ?? [];

  const current = variants.find((v) => v.variant_id === variantId);
  const [quickQuery, setQuickQuery] = useState("");
  // If the URL's ship code is on this variant, use it; otherwise the dropdown
  // stays on "—" (unselected). Never auto-default to the first tail —
  // the URL is the source of truth for shareable deep links.
  const selectedCode =
    current?.tails?.some((t) => t.code === urlCode) ? urlCode : "";

  useEffect(() => {
    setQuickQuery("");
  }, [variantId]);

  const uniqueFleets = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of variants) {
      if (!seen.has(v.fleet_ref)) {
        seen.add(v.fleet_ref);
        out.push(v.fleet_ref);
      }
    }
    // Alphabetical for scannability — arrival order is DB order.
    return out.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [variants]);

  const tailOptions = current?.tails ?? [];

  // Search suggestions: fleets first (alphabetical), then every tail as
  // "code - tail - fleet" sorted by code — ship code is what people type.
  const quickJumpEntries = useMemo(() => {
    const fleets: { label: string; variantId: string; code?: string }[] = [];
    const tails: { label: string; variantId: string; code?: string }[] = [];
    for (const v of variants) {
      fleets.push({ label: v.name, variantId: v.variant_id });
      for (const t of v.tails ?? []) {
        tails.push({
          label: `${t.code} - ${t.tail} - ${v.name}`,
          variantId: v.variant_id,
          code: t.code,
        });
      }
    }
    const alpha = (a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label, undefined, { numeric: true });
    return [...fleets.sort(alpha), ...tails.sort(alpha)];
  }, [variants]);

  const goToEntry = (e: { variantId: string; code?: string }) => {
    const suffix = e.code ? `?code=${encodeURIComponent(e.code)}` : "";
    navigate(`/fleet/${e.variantId}${suffix}`);
  };

  const tryNavigateFromQuery = (raw: string) => {
    if (!raw) return;
    const hit = quickJumpEntries.find((e) => e.label === raw);
    if (hit) { goToEntry(hit); return; }
    const q = raw.toLowerCase();
    const partial = quickJumpEntries.find((e) =>
      e.label.toLowerCase().includes(q),
    );
    if (partial) goToEntry(partial);
  };

  const datalistId = useId();

  const onFleetChange = (fleetRef: string) => {
    const first = variants.find((v) => v.fleet_ref === fleetRef);
    if (first) navigate(`/fleet/${first.variant_id}`);
  };

  return (
    <header className="sticky top-0 z-50 border-b-2 border-slate-700 bg-slate-800 text-slate-100">
      <div className="flex flex-col gap-2 px-6 py-2.5">
        {/* Single row: Title + Fleet + Tail + Search + Stats + User */}
        <div className="flex items-center gap-3">
          <h1 className="m-0 border-r border-slate-700 pr-4 text-base font-bold leading-tight tracking-tight">
            <span className="text-sky-400">Interactive</span> LOPA
          </h1>

          <Field label="Fleet-Code">
            <select
              className="min-w-[160px] cursor-pointer rounded border-0 bg-white px-2 py-1 text-[12.5px] text-slate-800 focus:outline focus:outline-2 focus:outline-sky-500"
              value={current?.fleet_ref ?? ""}
              onChange={(e) => onFleetChange(e.target.value)}
              disabled={isLoading || isError}
            >
              <option value="">—</option>
              {uniqueFleets.map((fr) => (
                <option key={fr} value={fr}>{fr}</option>
              ))}
            </select>
          </Field>

          <Field label="Tail">
            <select
              className="min-w-[160px] cursor-pointer rounded border-0 bg-white px-2 py-1 text-[12.5px] text-slate-800 focus:outline focus:outline-2 focus:outline-sky-500 disabled:cursor-not-allowed disabled:bg-slate-300"
              disabled={tailOptions.length === 0}
              value={selectedCode}
              onChange={(e) => {
                const next = new URLSearchParams(searchParams);
                const v = e.target.value;
                if (v) next.set("code", v);
                else    next.delete("code");
                setSearchParams(next, { replace: false });
              }}
            >
              {tailOptions.length === 0 ? (
                <option value="">—</option>
              ) : (
                <>
                  <option value="">— (any)</option>
                  {tailOptions.map((t) => (
                    <option key={t.tail} value={t.code}>
                      {t.code} - {t.tail}
                    </option>
                  ))}
                </>
              )}
            </select>
          </Field>

          {/* Search — right side group */}
          <span className="quick-jump ml-2">
            <input
              type="search"
              list={datalistId}
              autoComplete="off"
              placeholder="Search…"
              className="w-[200px] rounded border-0 bg-white px-2 py-1 text-[12.5px] text-slate-800 focus:outline focus:outline-2 focus:outline-sky-500"
              value={quickQuery}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setQuickQuery(v);
                const hit = quickJumpEntries.find((entry) => entry.label === v);
                if (hit) goToEntry(hit);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  tryNavigateFromQuery(quickQuery);
                }
              }}
            />
            <datalist id={datalistId}>
              {quickJumpEntries.map((entry, i) => (
                <option key={i} value={entry.label} />
              ))}
            </datalist>
          </span>

          {isError && (
            <span className="ml-auto text-xs text-red-300">
              Couldn't load fleets — is the API running?
            </span>
          )}
          {!isError && variants.length > 0 && (
            <span className="ml-auto text-[11.5px] text-slate-400">
              {variants.length} variants · {totalTails(variants)} tails
            </span>
          )}

          {authOn && me.data && (
            <div className="flex items-center gap-2 border-l border-slate-700 pl-3">
              <span className="text-[11.5px] text-slate-300" title={me.data.email}>
                {me.data.email}
              </span>
              <button
                onClick={() => logout.mutate()}
                className="rounded bg-slate-700 px-2.5 py-1 text-[11.5px] font-semibold text-slate-200 hover:bg-slate-600"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}


function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-300">
      {label}:
      {children}
    </label>
  );
}


function totalTails(variants: Variant[]): number {
  return variants.reduce((acc, v) => acc + (v.tails?.length ?? 0), 0);
}
